import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { errorText } from '@mezha/api'
import { usePlatformLoginMutation } from '../../api/endpoints/auth'
import { fromFailure, type ApiFailure } from '../../api/baseApi'
import { useEnter } from '../../app/enter'
import { useAppSelector } from '../../app/store'
import { PLATFORM_NAME, type Platform } from '../../platform/detect'
import { yandexReady, yandexSignature } from '../../platform/yandex'
import { PlankButton } from '../../ui'
import { AuthFrame, FormError } from './AuthFrame'
import { VkEntry } from './VkEntry'

type Phase = 'idle' | 'working' | 'failed'

/** Entry on VK and Yandex Games: only the platform's signed identity, never a
 *  password form or a guest mode. VK signs in by itself; Yandex waits for «Играть». */
export function PlatformEntry({ platform }: { platform: Exclude<Platform, 'web'> }) {
  return platform === 'vk' ? <VkEntry /> : <YandexEntry />
}

function YandexEntry() {
  const platform = 'yandex'
  const [phase, setPhase] = useState<Phase>('idle')
  const [error, setError] = useState<string | null>(null)
  const [signIn] = usePlatformLoginMutation()
  const enter = useEnter()
  const signedIn = useAppSelector(s => s.session.signedIn)
  const back = (useLocation().state as { from?: string } | null)?.from

  const run = useCallback(async () => {
    setPhase('working')
    setError(null)
    try {
      if (!signedIn) {
        const credential = await yandexSignature()
        await signIn({ platform, credential }).unwrap()
      }
      await enter(back)
    } catch (err) {
      setPhase('failed')
      if (err && typeof err === 'object' && 'code' in err) {
        const f = fromFailure(err as ApiFailure)
        setError(f.code === 'provider_not_configured'
          ? `Вход через ${PLATFORM_NAME[platform]} сейчас недоступен (${f.code})`
          : f.code === 'invalid_signature' ? `${PLATFORM_NAME[platform]} не подтвердили вход` : errorText(f))
      } else {
        setError('Яндекс Игры не выдали данные игрока')
      }
    }
  }, [back, enter, signIn, signedIn])

  useEffect(() => {
    void yandexReady()
  }, [])

  return (
    <AuthFrame title="Межа" sub={`Где-то в тридесятом царстве · ${PLATFORM_NAME[platform]}`}>
      {phase === 'working' && <p className="auth__lead" role="status">Входим через {PLATFORM_NAME[platform]}…</p>}
      {phase === 'idle' && <p className="auth__lead">Край ждёт. Нажмите «Играть», чтобы войти учётной записью площадки.</p>}
      <FormError text={error} />
      {phase !== 'working' && (
        <div className="auth__actions">
          <PlankButton gold onClick={() => void run()}>{phase === 'failed' ? 'Повторить' : 'Играть'}</PlankButton>
        </div>
      )}
    </AuthFrame>
  )
}
